import { forwardRef, useEffect, useImperativeHandle, type ReactNode } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { hubProseClassName } from '../../utils/hubProse';
import {
  BoldIcon,
  ItalicIcon,
  ListBulletIcon,
  NumberedListIcon,
  LinkIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

export type RichTextEditorHandle = {
  insertImage: (url: string, alt: string) => void;
};

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  onUploadImageClick?: () => void;
  uploadingImage?: boolean;
}

function ToolbarButton({
  active,
  disabled,
  title,
  onClick,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  title: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`rounded p-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        active
          ? 'bg-primary/10 text-primary'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  );
}

const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  (
    {
      value,
      onChange,
      label = 'Body',
      placeholder = 'Write section content…',
      disabled = false,
      onUploadImageClick,
      uploadingImage = false,
    },
    ref
  ) => {
    const editor = useEditor({
      immediatelyRender: false,
      extensions: [
        StarterKit.configure({
          heading: { levels: [2, 3] },
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        }),
        Image.configure({
          HTMLAttributes: { class: 'rounded-lg max-w-full' },
        }),
        Placeholder.configure({ placeholder }),
      ],
      content: value || '',
      editable: !disabled,
      editorProps: {
        attributes: {
          'data-placeholder': placeholder,
          class:
            `rich-text-editor-content min-h-[180px] px-3 py-2 focus:outline-none ${hubProseClassName} prose-sm`,
        },
      },
      onUpdate: ({ editor: currentEditor }) => {
        const html = currentEditor.getHTML();
        onChange(html === '<p></p>' ? '' : html);
      },
    });

    useEffect(() => {
      if (!editor) return;
      editor.setEditable(!disabled);
    }, [editor, disabled]);

    useImperativeHandle(ref, () => ({
      insertImage: (url: string, alt: string) => {
        editor?.chain().focus().setImage({ src: url, alt }).run();
      },
    }));

    const setLink = () => {
      if (!editor) return;
      const previousUrl = editor.getAttributes('link').href as string | undefined;
      const url = window.prompt('Link URL', previousUrl || 'https://');
      if (url === null) return;
      if (url.trim() === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
    };

    return (
      <div>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        )}
        <div
          className={`rounded-md border border-gray-300 bg-white focus-within:border-primary focus-within:ring-1 focus-within:ring-primary ${
            disabled ? 'opacity-60' : ''
          }`}
        >
          {editor && (
            <div className="flex flex-wrap items-center gap-0.5 border-b border-gray-200 px-2 py-1.5">
              <ToolbarButton
                title="Bold"
                active={editor.isActive('bold')}
                disabled={disabled}
                onClick={() => editor.chain().focus().toggleBold().run()}
              >
                <BoldIcon className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton
                title="Italic"
                active={editor.isActive('italic')}
                disabled={disabled}
                onClick={() => editor.chain().focus().toggleItalic().run()}
              >
                <ItalicIcon className="h-4 w-4" />
              </ToolbarButton>
              <span className="mx-1 h-5 w-px bg-gray-200" aria-hidden />
              <ToolbarButton
                title="Heading 2"
                active={editor.isActive('heading', { level: 2 })}
                disabled={disabled}
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              >
                <span className="text-xs font-bold px-0.5">H2</span>
              </ToolbarButton>
              <ToolbarButton
                title="Heading 3"
                active={editor.isActive('heading', { level: 3 })}
                disabled={disabled}
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              >
                <span className="text-xs font-bold px-0.5">H3</span>
              </ToolbarButton>
              <span className="mx-1 h-5 w-px bg-gray-200" aria-hidden />
              <ToolbarButton
                title="Bullet list"
                active={editor.isActive('bulletList')}
                disabled={disabled}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
              >
                <ListBulletIcon className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton
                title="Numbered list"
                active={editor.isActive('orderedList')}
                disabled={disabled}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
              >
                <NumberedListIcon className="h-4 w-4" />
              </ToolbarButton>
              <span className="mx-1 h-5 w-px bg-gray-200" aria-hidden />
              <ToolbarButton
                title="Link"
                active={editor.isActive('link')}
                disabled={disabled}
                onClick={setLink}
              >
                <LinkIcon className="h-4 w-4" />
              </ToolbarButton>
              {onUploadImageClick && (
                <ToolbarButton
                  title="Upload image"
                  disabled={disabled || uploadingImage}
                  onClick={onUploadImageClick}
                >
                  <PhotoIcon className="h-4 w-4" />
                </ToolbarButton>
              )}
            </div>
          )}
          <EditorContent editor={editor} />
        </div>
        <style>{`
          .rich-text-editor-content p.is-editor-empty:first-child::before {
            color: #9ca3af;
            content: attr(data-placeholder);
            float: left;
            height: 0;
            pointer-events: none;
          }
        `}</style>
      </div>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
